import { pxToEm } from 'util/pxToEm';
import React, { ReactElement } from 'react';
import { Box, Typography, useTheme, Link } from '@mui/material';
import { CampusLushLogo } from 'components/ui';
import { Routes } from 'services/routes';
import { FacebookIcon, InstagramIcon, LinkedInIcon } from 'components/icons';

export interface FooterProps {
  variant?: 'primary' | 'white';
}

export const Footer = ({ variant = 'primary' }: FooterProps): ReactElement => {
  const theme = useTheme();

  const socialMediaIcons = (
    <>
      <Link href="https://www.instagram.com/campuslush/" target="_blank">
        <InstagramIcon style={{ marginRight: 32 }} />
      </Link>

      <Link href="https://www.facebook.com/campuslush" target="_blank">
        <FacebookIcon style={{ marginRight: 32 }} />
      </Link>

      <Link href="https://www.linkedin.com/company/campuslush/" target="_blank">
        <LinkedInIcon />
      </Link>
    </>
  );

  const mainLinks = (
    <Box
      fontWeight={variant === 'primary' ? 600 : 500}
      color={variant === 'primary' ? theme.palette.primary.main : '#fff'}
      flexDirection={{ xs: 'column', md: 'row' }}
      alignItems={{ xs: 'unset', md: 'center' }}
      gap={{ xs: 24, md: 12 }}
      sx={{
        display: 'flex',
        justifyContent: 'center',
        fontSize: pxToEm(12),
      }}
    >
      <a
        href={Routes.logIn()}
        style={{ color: 'inherit', textDecoration: 'underline' }}
      >
        Login
      </a>
      <a
        href={Routes.signUp()}
        style={{ color: 'inherit', textDecoration: 'underline' }}
      >
        Create Account
      </a>
      <a
        href={Routes.external.marketing.about()}
        style={{ color: 'inherit', textDecoration: 'underline' }}
      >
        For Schools
      </a>
      <a
        href={Routes.external.marketing.contact()}
        style={{ color: 'inherit', textDecoration: 'underline' }}
      >
        Contact Us
      </a>
    </Box>
  );

  return (
    <Box
      sx={{
        background: '#23305F',
      }}
    >
      <Box
        width={{ md: 'unset', lg: 1280 }}
        sx={{
          p: '32px 20px',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          margin: 'auto',
        }}
      >
        <Box display="flex">
          <Link
            href={Routes.home()}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
            }}
          >
            <CampusLushLogo
              sx={{
                fontSize: '1em',
                '& .CampusLushLogo-text': {
                  color: theme =>
                    variant === 'primary' ? theme.palette.primary.main : '#fff',
                },
              }}
            />
          </Link>

          <Box display={{ xs: 'none', md: 'flex' }} ml="auto">
            {mainLinks}
          </Box>
        </Box>
        <Box mt={8} mb={{ xs: 30, md: 48 }}>
          <Typography
            sx={{
              color: '#fff',
              fontWeight: 500,
              fontSize: pxToEm(12),
            }}
          >
            Bringing Schools, Students and Alumni Together
          </Typography>
        </Box>
        <Box display={{ xs: 'block', md: 'none' }}>{mainLinks}</Box>
        <Box
          display={{ xs: 'display', md: 'none' }}
          sx={{
            fontSize: pxToEm(24),
            mt: 40,
            mb: 24,
          }}
        >
          {socialMediaIcons}
        </Box>
        <Box
          display="flex"
          flexDirection={{ xs: 'column', md: 'row' }}
          alignItems={{ xs: 'unset', md: 'center' }}
        >
          <Typography
            sx={{
              whiteSpace: 'nowrap',
              color:
                variant === 'primary' ? theme.palette.neutral.darkest : '#fff',
              fontWeight: 500,
              fontSize: pxToEm(12),
            }}
          >
            © {new Date().getFullYear()} CampusLush LLC. All rights reserved.
          </Typography>
          <Box
            fontWeight={variant === 'primary' ? 600 : 500}
            color={variant === 'primary' ? theme.palette.primary.main : '#fff'}
            sx={{
              display: 'flex',
              gap: 12,
              fontSize: pxToEm(12),
            }}
          >
            <Box component="span" display={{ xs: 'none', md: 'inline-block' }}>
              <Typography>&nbsp;&nbsp;&nbsp;|</Typography>
            </Box>
            <a
              target="_blank"
              href={Routes.external.marketing.terms()}
              style={{ color: 'inherit', textDecoration: 'underline' }}
              rel="noreferrer"
            >
              Terms
            </a>
            <Typography>|</Typography>
            <a
              target="_blank"
              href={Routes.external.marketing.privacy()}
              style={{ color: 'inherit', textDecoration: 'underline' }}
              rel="noreferrer"
            >
              Privacy
            </a>
          </Box>
          <Box
            display={{ xs: 'none', md: 'flex' }}
            sx={{
              alignItems: 'center',
              ml: 'auto',
              fontSize: pxToEm(24),
            }}
          >
            {socialMediaIcons}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};
